import React from "react";
import { MenuItem, Select, TextField, Typography, Box } from "@mui/material";
import { useForm, Controller } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import NavigationBtns from "../ui/NavigationBtns";
import { useAuth } from "../contexts/AuthProvider";
import { useTranslation } from "react-i18next";
import Spinner from "../ui/Spinner";
import { gender } from "../data/data";
import MainLayout from "../ui/MainLayout";
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DemoContainer } from '@mui/x-date-pickers/internals/demo';
import { useNavigation } from "../contexts/NavigationProvider";
import { handleNext } from "../utility/navigationUtils";
import { useNavigate } from "react-router-dom";

const schema = z.object({
    fullNameEnglish: z.string().min(1, "Full name in English is required."),
    fullNameArabic: z.string().min(1, "Full name in Arabic is required."),
    dateofBirth: z.string().min(1, "Date of birth is required."),
    placeofBirth: z.string().min(1, "Place of birth is required."),
    gender: z.string().min(1, "Gender is required."),
    nationalIDNumber: z.string().optional(),
    placeofIssue: z.string().min(1, "Place of issue is required."),
    dateofIssue: z.string().min(1, "Date of issue is required."),
    dateofExpiry: z.string().min(1, "Date of expiry is required."),
});

const NationalNumberInfo: React.FC = () => {
    const { documentData, submittedData, setSubmittedData, setDocumentData } = useAuth();
    const { t, i18n } = useTranslation();
    const { setCurrentStep, currentStep, setDone, steps, setError, setPersonalInfoStep } = useNavigation();
    const navigate = useNavigate();
    const contextValue = useAuth();
    const schema = z.object({
        fullNameEnglish: z.string(),
        fullNameArabic: z.string().min(1, {
            message:
                i18n.language === "en"
                    ? "Full name in Arabic is required."
                    : "الاسم الكامل باللغة العربية مطلوب.",
        }),
        dateofBirth: z.string().min(1, {
            message:
                i18n.language === "en"
                    ? "Date of birth is required."
                    : "تاريخ الميلاد مطلوب.",
        }),
        placeofBirth: z.string().min(1, {
            message:
                i18n.language === "en"
                    ? "Place of birth is required."
                    : "مكان الميلاد مطلوب.",
        }),
        gender: z.string().length(1, {
            message: i18n.language === "en" ? "Gender is required." : "الجنس مطلوب.",
        }),
        nationalIDNumber: z
            .string()
            .min(1, {
                message:
                    i18n.language === "en"
                        ? "National ID number is required."
                        : "الرقم القومي مطلوب.",
            })
            .optional(),
        placeofIssue: z.string().min(1, {
            message:
                i18n.language === "en"
                    ? "Place of issue is required."
                    : "مكان الإصدار مطلوب.",
        }),
        dateofIssue: z.string().min(1, {
            message:
                i18n.language === "en"
                    ? "Date of issue is required."
                    : "تاريخ الإصدار مطلوب.",
        }),
        dateofexpiry: z.string().min(1, {
            message:
                i18n.language === "en"
                    ? "Date of expiry is required."
                    : "تاريخ الانتهاء مطلوب.",
        }),
        identityNumber: z.string(),
    });

    type FormFields = z.infer<typeof schema>;
    const {
        handleSubmit,
        formState: { isSubmitting, errors },
        trigger,
        control,
        register,
    } = useForm<FormFields>({
        resolver: zodResolver(schema),
        defaultValues: {
            fullNameEnglish: documentData.fullName,
            fullNameArabic: documentData.fullNameArabic,
            dateofBirth: documentData.dateOfBirthFormatted?.toString() ?? "",
            placeofBirth: documentData.placeOfBirth,
            gender: documentData.sex,
            identityNumber: documentData.identityNumber,
            placeofIssue: documentData.placeOfIssue,
            dateofIssue: documentData.issueDateFormatted?.toString() ?? "",
            dateofexpiry: documentData.dateOfExpiryFormatted.toString(),
        },
    });

    const submitFunction = async (formdata: FormFields) => {

        // setting the values from form
        setSubmittedData({
            ...submittedData,
            fullNameArabic: formdata.fullNameArabic,
            fullNameEnglish: formdata.fullNameEnglish,
            dateofBirth: formdata.dateofBirth,
            placeofBirth: formdata.placeofBirth,
            gender: formdata.gender,
            identityNumber: formdata.identityNumber,
            placeofIssue: formdata.placeofIssue,
            dateofIssue: formdata.dateofIssue,
            dateofexpiry: formdata.dateofexpiry ?? "",
        });
        setDocumentData({
            ...documentData,
            fullName: formdata.fullNameEnglish,
            fullNameArabic: formdata.fullNameArabic,
            placeOfBirth: formdata.placeofBirth,
            issueDateFormatted: new Date(formdata.dateofIssue),
            dateOfBirthFormatted: new Date(formdata.dateofBirth),
            dateOfExpiryFormatted: new Date(formdata.dateofexpiry),
            documentNumber: formdata.identityNumber,
            identityNumber: formdata.nationalIDNumber ?? "",
            sex: formdata.gender,
            nationality: "",
            placeOfIssue: formdata.placeofIssue,
            address: "",
            nationalIDNumber: formdata.nationalIDNumber ?? "",
        });

        // setting the current stepذ
        setPersonalInfoStep(1);
        handleNext(setCurrentStep, 7, steps, navigate);
    };
    return (
        <>
            {isSubmitting ? <Spinner /> : null}
            <MainLayout>
                <Box
                    component="div"
                    sx={{
                        position: "relative",
                        display: "flex",
                        flexDirection: "column",
                        flexGrow: 1
                    }}
                >
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <DemoContainer components={['DatePicker']}>

                            <form
                                onSubmit={handleSubmit(submitFunction)}
                                style={{
                                    display: "flex",
                                    flexDirection: "column",
                                    justifyContent: "space-between",
                                    height: "100%",
                                    flexGrow: 1,
                                }}
                            >
                                <Typography variant="body3" color="initial" mt={2} p={0} pb={2} sx={{ color: '#7d0087' }}>
                                    {t("verifyInformationNationalNumber")}
                                </Typography>
                                <Typography variant="body1" color="initial" m={0} p={0} fontSize={10}>
                                    {t("fullNameEnglish")}
                                </Typography>
                                <TextField
                                    type="text"
                                    id="fullNameEnglish"
                                    {...register("fullNameEnglish")}
                                    error={errors.fullNameEnglish?.message !== undefined}
                                    helperText={
                                        errors.fullNameEnglish ? errors.fullNameEnglish.message : ""
                                    }
                                    sx={{ borderRadius: "10px", margin: 0, fontSize: "12px" }}
                                    onBlur={async () => {
                                        await trigger("fullNameEnglish");
                                    }}
                                />
                                <Typography variant="body1" color="initial" m={0} p={0} fontSize={10}>
                                    {t("fullNameArabic")}
                                </Typography>
                                <TextField
                                    type="text"
                                    id="fullNameArabic"
                                    {...register("fullNameArabic")}
                                    error={errors.fullNameArabic?.message !== undefined}
                                    helperText={
                                        errors.fullNameArabic ? errors.fullNameArabic.message : ""
                                    }
                                    sx={{ borderRadius: "10px", margin: 0, fontSize: "12px" }}
                                    onBlur={async () => {
                                        await trigger("fullNameArabic");
                                    }}
                                />
                                <Typography variant="body1" color="initial" m={0} p={0} fontSize={10}>
                                    {t("dateofBirth")}
                                </Typography>
                                {/* <TextField
                                    type="text"
                                    id="dateofBirth"
                                    {...register("dateofBirth")}
                                    error={errors.dateofBirth?.message !== undefined}
                                    helperText={errors.dateofBirth ? errors.dateofBirth.message : ""}
                                    sx={{ borderRadius: "10px", margin: 0, fontSize: "12px" }}
                                    onBlur={async () => await trigger("dateofBirth")}
                                /> */}
                                <DatePicker
                                    onChange={(newValue) => {
                                        documentData.dateOfBirthFormatted = newValue ? new Date(newValue.format()) : null;
                                    }}
                                />
                                <Typography variant="body1" color="initial" m={0} p={0} fontSize={10}>
                                    {t("placeofBirth")}
                                </Typography>
                                <TextField
                                    type="text"
                                    id="placeofBirth"
                                    {...register("placeofBirth")}
                                    error={errors.placeofBirth?.message !== undefined}
                                    helperText={errors.placeofBirth ? errors.placeofBirth.message : ""}
                                    sx={{ borderRadius: "10px", margin: 0, fontSize: "12px" }}
                                    onBlur={async () => await trigger("placeofBirth")}
                                />
                                <Typography variant="body1" color="initial" m={0} p={0} fontSize={10}>
                                    {t("gender")}
                                </Typography>
                                <Controller
                                    name="gender"
                                    control={control}
                                    render={({ field }) => (
                                        <Select
                                            id="gender"
                                            {...field}
                                            error={errors.gender?.message !== undefined}
                                            sx={{ width: "100%" }}
                                            displayEmpty
                                            inputProps={{ "aria-label": "Select gender" }}
                                            onBlur={async () => await trigger("gender")}
                                        >
                                            <MenuItem value="">
                                                {t("select gender")}
                                            </MenuItem>
                                            {gender.map((gender) => (
                                                <MenuItem key={gender.id} value={gender.id}>
                                                    <Typography variant="body2" color="black">
                                                        {t(`${gender.title}`)}
                                                    </Typography>
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    )}
                                />
                                <Typography variant="body1" color="error">
                                    {errors.gender ? errors.gender.message : ""}
                                </Typography>
                                <Typography variant="body1" color="initial" m={0} p={0} fontSize={10}>
                                    {t("nationalIDNumber")}
                                </Typography>
                                <TextField
                                    type="text"
                                    id="identityNumber"
                                    maxLength={15}
                                    error={errors.identityNumber?.message !== undefined}
                                    helperText={
                                        errors.identityNumber ? errors.identityNumber.message : ""
                                    }
                                    {...register("identityNumber")}
                                    sx={{ borderRadius: "10px", margin: 0, fontSize: "12px" }}
                                    onBlur={async () => await trigger("identityNumber")}
                                />
                                <Typography variant="body1" color="initial" m={0} p={0} fontSize={10}>
                                    {t("placeofIssue")}
                                </Typography>
                                <TextField
                                    type="text"
                                    id="placeofIssue"
                                    error={errors.placeofIssue?.message !== undefined}
                                    helperText={errors.placeofIssue ? errors.placeofIssue.message : ""}
                                    {...register("placeofIssue")}
                                    sx={{ borderRadius: "10px", margin: 0, fontSize: "12px" }}
                                    onBlur={async () => await trigger("placeofIssue")}
                                />
                                <Typography variant="body1" color="initial" m={0} p={0} fontSize={10}>
                                    {t("dateofIssue")}
                                </Typography>
                                {/* <TextField
                                    type="text"
                                    id="dateofIssue"
                                    error={errors.dateofIssue?.message !== undefined}
                                    helperText={errors.dateofIssue ? errors.dateofIssue.message : ""}
                                    {...register("dateofIssue")}
                                    sx={{ borderRadius: "10px", margin: 0, fontSize: "12px" }}
                                    onBlur={async () => await trigger("dateofIssue")}
                                /> */}
                                <DatePicker
                                    onChange={(newValue) => {
                                        documentData.issueDateFormatted = newValue ? new Date(newValue.format()) : null;
                                    }}
                                />
                                <NavigationBtns isSubmitting={isSubmitting} />
                            </form>
                        </DemoContainer>
                    </LocalizationProvider>
                </Box>
            </MainLayout>
        </>
    );
};

export default NationalNumberInfo;
